import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../../components/Layout';
import PrioritizeDetailModal, {
  PrioritizeItem,
  FeatureStatus,
  HealthState,
} from '../../components/PrioritizeDetailModal';
import AddLinkedFeaturePopup from '../../components/AddLinkedFeaturePopup';
import BoardControlsButton, { BoardPrefs, loadBoardPrefs, isColumnVisible } from '../../components/BoardControlsButton';
import { prioritizeService } from '../../services/prioritizeService';
import { organizationService, OrgMember } from '../../services/organizationService';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { useActiveProject } from '../../hooks/useActiveProject';
import { useFeatureLinkData } from '../../hooks/useFeatureLinkData';
import '../../styles/Pages.css';
import '../../styles/CustomersList.css';
import '../../styles/PrioritizeModal.css';
import '../../styles/InitiativesPage.css';
import '../../styles/ObjectivesPage.css';

const STATUS_PILL_STYLE: Record<FeatureStatus, { bg: string; fg: string; label: string }> = {
  'new-idea':    { bg: '#ccfbf1', fg: '#115e59', label: 'New idea' },
  'candidate':   { bg: '#f3e8ff', fg: '#6b21a8', label: 'Candidate' },
  'planned':     { bg: '#dbeafe', fg: '#1e40af', label: 'Planned' },
  'in-progress': { bg: '#fef3c7', fg: '#92400e', label: 'In Progress' },
  'released':    { bg: '#d1fae5', fg: '#047857', label: 'Released' },
  'backlog':     { bg: '#f3f4f6', fg: '#4b5563', label: 'Backlog' },
  'done':        { bg: '#d1fae5', fg: '#047857', label: 'Done' },
  'upcoming':    { bg: '#dbeafe', fg: '#1e40af', label: 'Upcoming' },
  'completed':   { bg: '#d1fae5', fg: '#047857', label: 'Completed' },
};
const HEALTH_STYLE: Record<HealthState, { color: string; label: string }> = {
  'on-track':  { color: '#10B981', label: 'On track' },
  'at-risk':   { color: '#F97316', label: 'At risk' },
  'off-track': { color: '#EF4444', label: 'Off track' },
  'none':      { color: '#9ca3af', label: '—' },
};

// Health updates store an icon name ('up' | 'wave' | 'down'); map it to a HealthState.
const HEALTH_ICON_TO_STATE: Record<string, HealthState> = {
  up: 'on-track', wave: 'at-risk', down: 'off-track',
};

// Feature status → completeness %, for objective work-progress auto-rollup.
const FEATURE_COMPLETENESS: Record<string, number> = {
  'new-idea': 0, 'candidate': 25, 'planned': 50, 'in-progress': 75,
  'backlog': 0, 'done': 100, 'released': 100,
};

const uid = () => Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);

const initialsOf = (name?: string | null) =>
  (name || '?').trim().split(/\s+/).slice(0, 2).map(s => s[0]).join('').toUpperCase() || '?';

type ColKey = 'health' | 'owner' | 'team' | 'timeframe' | 'status' | 'progress' | 'effort' | 'bv';
const COLUMNS: { key: ColKey; label: string; width: number }[] = [
  { key: 'health',    label: 'Health',          width: 230 },
  { key: 'owner',     label: 'Owner',           width: 120 },
  { key: 'team',      label: 'Team',            width: 130 },
  { key: 'timeframe', label: 'Timeframe',       width: 150 },
  { key: 'status',    label: 'Status',          width: 140 },
  { key: 'progress',  label: '% Work progress', width: 190 },
  { key: 'effort',    label: 'Effort',          width: 120 },
  { key: 'bv',        label: 'Business Value',  width: 140 },
];

/* Target-circle marker (objective) — blue or amber per item color */
const TargetMarker: React.FC<{ color?: string }> = ({ color = '#3b82f6' }) => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
    <circle cx="12" cy="12" r="9" />
    <circle cx="12" cy="12" r="3" fill={color} stroke="none" />
  </svg>
);
/* Square marker (sub-feature linked to an objective) */
const SquareMarker: React.FC<{ color?: string }> = ({ color = 'var(--brand-500)' }) => (
  <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 3, background: color, flexShrink: 0 }} />
);

const Objectives: React.FC = () => {
  const { currentOrg } = useAuth();
  const { can } = usePermissions();
  const canManage = can('product_objectives');
  const { activeProjectId } = useActiveProject();
  const { companies, customerUsers, documents } = useFeatureLinkData(currentOrg?.id, activeProjectId);

  const [items, setItems] = useState<PrioritizeItem[]>([]);
  const [members, setMembers] = useState<OrgMember[]>([]);
  const [search, setSearch] = useState('');
  const [draft, setDraft] = useState<PrioritizeItem | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [pickerFor, setPickerFor] = useState<{ id: string; top: number; left: number } | null>(null);
  const [boardPrefs, setBoardPrefs] = useState<BoardPrefs>(() => loadBoardPrefs('objectives:boardControls'));
  const visibleColumns = COLUMNS.filter(c => isColumnVisible(boardPrefs.columns, c.key));
  const toggleExpand = (id: string) => setExpanded(prev => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!currentOrg?.id) return;
      try {
        const rows = await prioritizeService.getItems(activeProjectId);
        if (!cancelled) setItems(rows);
      } catch (err) {
        console.error('Failed to load objectives:', err);
        if (!cancelled) setItems([]);
      }
    })();
    return () => { cancelled = true; };
  }, [currentOrg?.id, activeProjectId]);

  useEffect(() => {
    if (!currentOrg?.id) return;
    organizationService.getMembersWithProfiles(currentOrg.id).then(setMembers).catch(() => {});
  }, [currentOrg?.id]);

  const memberById = (id?: string | null) => id ? members.find(m => m.user_id === id) ?? null : null;

  const q = search.trim().toLowerCase();
  const objectives = useMemo(
    () => items.filter(i => i.kind === 'objective' && (!q || i.name.toLowerCase().includes(q))),
    [items, q],
  );

  const featuresOfObjective = (objectiveId: string) =>
    items.filter(i => i.kind === 'feature' && i.objectiveParentId === objectiveId);

  /* Default-expand objectives on first load so linked features are visible. */
  useEffect(() => {
    setExpanded(prev => {
      if (prev.size > 0) return prev;
      const n = new Set<string>();
      items.forEach(i => { if (i.kind === 'objective') n.add(i.id); });
      return n;
    });
  }, [items.length === 0]);

  const parentCandidates = useMemo(
    () => items.filter(i => i.kind === 'product' || i.kind === 'epic'),
    [items],
  );

  // Build a unique objective name by appending " 1", " 2", … if needed.
  const uniqueName = (base: string, excludeId?: string): string => {
    const taken = new Set(
      items.filter(i => i.kind === 'objective' && i.id !== excludeId).map(i => i.name.trim().toLowerCase()),
    );
    if (!taken.has(base.trim().toLowerCase())) return base;
    let n = 1;
    while (taken.has(`${base} ${n}`.trim().toLowerCase())) n++;
    return `${base} ${n}`;
  };

  const handleSave = (updated: PrioritizeItem) => {
    // Objectives can't share a name with another objective.
    const clash = items.some(i => i.kind === 'objective' && i.id !== updated.id && i.name.trim().toLowerCase() === updated.name.trim().toLowerCase());
    if (clash) { alert('An objective with this name already exists. Please choose a unique name.'); return; }
    setItems(prev => prev.map(i => i.id === updated.id ? updated : i));
    setDraft(null);
    prioritizeService.updateItem(updated.id, updated).catch(err => {
      console.error('Failed to save objective:', err);
      alert(`Failed to save: ${err?.message ?? err}`);
    });
  };

  const handleDelete = (id: string) => {
    setItems(prev => prev.filter(i => i.id !== id));
    setDraft(null);
    prioritizeService.deleteItems([id]).catch(err => {
      console.error('Failed to delete objective:', err);
      alert(`Failed to delete: ${err?.message ?? err}`);
    });
  };

  // Link / unlink a feature to this objective (sets objectiveParentId).
  const toggleFeatureInObjective = (featureId: string, objectiveId: string, checked: boolean) => {
    const feature = items.find(i => i.id === featureId);
    if (!feature) return;
    const updated = { ...feature, objectiveParentId: checked ? objectiveId : null };
    setItems(prev => prev.map(i => i.id === featureId ? updated : i));
    if (checked) setExpanded(prev => new Set(prev).add(objectiveId));
    prioritizeService.updateItem(featureId, updated).catch(err => {
      console.error('Failed to link feature:', err);
      alert(`Failed to update: ${err?.message ?? err}`);
    });
  };

  const createObjective = async () => {
    const newItem: PrioritizeItem = {
      id: 'temp_' + uid(), kind: 'objective', name: uniqueName('Unnamed objective'),
      ownerId: null, importanceScore: 0, color: '#3b82f6', createdAt: new Date().toISOString(),
    };
    try {
      const saved = await prioritizeService.createItem(newItem, items.length);
      setItems(prev => [...prev, saved]);
      setDraft(saved);
    } catch (err) {
      console.error('Failed to create objective:', err);
      alert('Failed to create objective. Please try again.');
    }
  };

  // Linked features → objective work-progress (manual value, or status rollup).
  const objectiveProgress = (obj: PrioritizeItem): number => {
    if ((obj.workProgressMode ?? 'auto') === 'manual') return obj.workProgress ?? 0;
    const feats = items.filter(i => i.kind === 'feature' && i.objectiveParentId === obj.id);
    if (feats.length === 0) return obj.workProgress ?? 0;
    const total = feats.reduce((sum, f) => sum + (FEATURE_COMPLETENESS[f.status ?? 'new-idea'] ?? 0), 0);
    return Math.round(total / feats.length);
  };

  const renderCell = (item: PrioritizeItem, key: ColKey): React.ReactNode => {
    const NA = <span className="prm-not-assigned">Not assigned</span>;
    switch (key) {
      case 'health': {
        // Prefer the latest posted health update; fall back to the scalar field.
        const updates = item.healthUpdates ?? [];
        const latest = updates.length ? updates[updates.length - 1] : null;
        const state: HealthState = latest
          ? (HEALTH_ICON_TO_STATE[latest.status] ?? 'none')
          : (item.health || 'none');
        if (state === 'none') {
          if (item.kind !== 'objective') return NA;
          return (
            <span className="obj-health-empty">
              <span className="obj-health-rollup">Auto-rolled up from linked items</span>
              <span className="obj-health-hint">Click to manually update</span>
            </span>
          );
        }
        const h = HEALTH_STYLE[state];
        return (
          <span className="prm-list-health">
            <span className="prm-list-health-icon" style={{ background: h.color }} />
            <span>{h.label}</span>
            {latest?.at && <span className="prm-list-health-ago">{latest.at}</span>}
          </span>
        );
      }
      case 'owner': {
        const m = memberById(item.ownerId);
        return m ? <span className="ini-avatar" style={{ background: '#EC4899' }} title={m.full_name || m.email || ''}>{initialsOf(m.full_name || m.email)}</span> : <span />;
      }
      case 'team':
        return item.teams ? <span className="ini-avatar" style={{ background: '#64748b' }}>{initialsOf(item.teams.split(',')[0])}</span> : <span />;
      case 'timeframe': {
        // Objectives show a quarter-style timeframe; fall back to start month.
        if (item.startDate) {
          const d = new Date(item.startDate + 'T00:00:00');
          if (!isNaN(d.getTime())) {
            const qtr = Math.floor(d.getMonth() / 3) + 1;
            return <span style={{ color: '#111827', fontSize: 13 }}>Q{qtr}, {d.getFullYear()}</span>;
          }
        }
        return <span />;
      }
      case 'status': {
        if (!item.status) {
          return <span className="prm-list-pill" style={{ background: '#dbeafe', color: '#1e40af' }}><span className="prm-list-pill-bar" style={{ background: '#1e40af' }} />Upcoming</span>;
        }
        const c = STATUS_PILL_STYLE[item.status];
        return <span className="prm-list-pill" style={{ background: c.bg, color: c.fg }}><span className="prm-list-pill-bar" style={{ background: c.fg }} />{c.label}</span>;
      }
      case 'progress': {
        const pct = objectiveProgress(item);
        return <span className="prm-list-progress"><span className="prm-list-progress-track"><span className="prm-list-progress-fill" style={{ width: `${pct}%` }} /></span><span className="prm-list-progress-num">{pct}%</span></span>;
      }
      case 'effort':
        return item.effort != null ? <strong>{item.effort}</strong> : <span className="obj-arrow">↱</span>;
      case 'bv':
        return item.businessValueScore != null ? <strong>{item.businessValueScore}</strong> : <span />;
    }
  };

  const renderRow = (item: PrioritizeItem, depth: number, isObjectiveRow: boolean): React.ReactNode => {
    const children = isObjectiveRow ? featuresOfObjective(item.id) : [];
    const canExpand = children.length > 0;
    const isOpen = expanded.has(item.id);
    const indent = depth * 22;
    return (
      <React.Fragment key={item.id}>
        <tr className="prm-row" onClick={() => setDraft(item)}>
          <td className="prm-cell prm-cell-name">
            <div className="prm-cell-name-inner" style={{ paddingLeft: 8 + indent }}>
              <button
                className={`prm-expand-btn ${isOpen ? 'prm-expand-btn-open' : ''}`}
                onClick={e => { e.stopPropagation(); if (canExpand) toggleExpand(item.id); }}
                style={{ visibility: canExpand ? 'visible' : 'hidden' }}
              >
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
              </button>
              {isObjectiveRow ? <TargetMarker color={item.color || '#3b82f6'} /> : <SquareMarker color={item.color || 'var(--brand-500)'} />}
              <span className={`prm-row-name ${isObjectiveRow ? 'ini-name-bold' : ''}`}>{item.name}</span>
              {isObjectiveRow && canManage && (
                <button
                  className="ini-add-sub"
                  title="Add feature"
                  onClick={e => {
                    e.stopPropagation();
                    const r = (e.currentTarget as HTMLButtonElement).getBoundingClientRect();
                    setPickerFor({ id: item.id, top: r.bottom + 4, left: r.left });
                  }}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                </button>
              )}
            </div>
          </td>
          {visibleColumns.map(col => (
            <td key={col.key} className="prm-cell" style={{ width: col.width, minWidth: col.width }}>{renderCell(item, col.key)}</td>
          ))}
        </tr>
        {isObjectiveRow && isOpen && children.map(c => renderRow(c, depth + 1, false))}
      </React.Fragment>
    );
  };

  return (
    <Layout>
      <div className="customers-list-container">
        <div className="customers-header-row">
          <h1 className="customers-title">Objectives</h1>
          <div className="header-right-section">
            <div className="search-bar">
              <svg className="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
              <input type="text" placeholder="Search objectives…" value={search} onChange={e => setSearch(e.target.value)} className="search-input" />
            </div>
            <BoardControlsButton columns={COLUMNS.map(c => ({ key: c.key, label: c.label }))} storageKey="objectives:boardControls" prefs={boardPrefs} onChange={setBoardPrefs} />
            {canManage && (
              <button className="customers-add-btn btn-primary-cta" onClick={createObjective} title="Add a new objective">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                Add Objective
              </button>
            )}
          </div>
        </div>

        <div className="prm-list-wrap">
          <div className="prm-list-table-wrap">
            <table className="prm-list-table">
              <colgroup>
                <col style={{ width: 420, minWidth: 280 }} />
                {visibleColumns.map(c => <col key={c.key} style={{ width: c.width, minWidth: c.width }} />)}
              </colgroup>
              <thead>
                <tr>
                  <th className="prm-th prm-th-fixed"><span className="prm-th-label">Objectives</span></th>
                  {visibleColumns.map(c => <th key={c.key} className="prm-th"><span className="prm-th-label">{c.label}</span></th>)}
                </tr>
              </thead>
              <tbody>
                {objectives.length === 0 && <tr><td colSpan={visibleColumns.length + 1} className="prm-empty">No objectives yet</td></tr>}
                {objectives.map(obj => renderRow(obj, 0, true))}
              </tbody>
            </table>
          </div>

          {canManage && (
            <button className="ini-create-row" onClick={createObjective}>
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>
              Create objective
            </button>
          )}
        </div>

        {pickerFor && (
          <div className="rel-pick-anchor" style={{ top: pickerFor.top, left: pickerFor.left }}>
            <AddLinkedFeaturePopup
              items={items}
              parentId={pickerFor.id}
              linkField="objectiveParentId"
              onToggle={(fid, checked) => toggleFeatureInObjective(fid, pickerFor.id, checked)}
              onClose={() => setPickerFor(null)}
            />
          </div>
        )}

        {draft && (
          <PrioritizeDetailModal
            item={draft}
            parentCandidates={parentCandidates}
            members={members}
            allItems={items}
            companies={companies}
            customerUsers={customerUsers}
            documents={documents}
            onDelete={handleDelete}
            onToggleFeature={(fid, checked) => toggleFeatureInObjective(fid, draft.id, checked)}
            onClose={() => setDraft(null)}
            onSave={handleSave}
          />
        )}
      </div>
    </Layout>
  );
};

export default Objectives;
